import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { 
  AddNewProduct,
  EditProduct,
  Customers,
  Dashboard,
  Orders,
  Products,
  CategoryTable,
  Login,
  UserManagement,
  Plugin,
  Color,
  ProductTypesMain,
  CollectionTable,
  AnalyticsHome,
  PromoCodes,
  ReturnRequestMain,
  Stocks,
  InventoryMain,
  PurchaseOrder,
  Supplier,
  StockReservation,
  TranslationMain,
  StockTransfer,
  ExchangeRate,
  AbandonedCart,
} from "./pages";

import { 
  CategoryTranslation,
  CollectionTranslation,
  ProductTranslation,
  TagsTranslation,
} from "./components/translations";

import { RootLayout, SettingsLayout } from "./layouts";
import PrivateRoute from './PrivateRoute';
import { General, Account, Payment, LinkAccount, Password, PushNotification, TaxClass, Shipping, Status, InvoiceSettings, Warehouse } from "./components/settings";

import PromocodeUsage from "./pages/PromocodeUsage";
import useDetectDevTools from "./detectDevTools";


import PageList from "./themes/pages/pageList";
import PageEditorApp from "./themes/pages/pageEditorApp";

function App() {
  if (process.env.NODE_ENV === "production") {
    console.log(
      "%c⚠️ Security Warning ⚠️\n" +
      "%cNever execute any script in the console that could steal your data. %cProtect your store and account security! \n\n" +
      "%cHere are some key guidelines to stay secure:\n" +
      "%c1. Never share your cookies, session keys, or any sensitive data such as access tokens or refresh tokens. \n" +
      "%c2. Avoid revealing your personal or store-related information to untrusted sources. \n" +
      "%c3. Be cautious of unsolicited requests asking for credentials or sensitive information. \n" +
      "%c4. Never paste anything here that could be risky or potentially used to steal your data. \n\n" +
      "%cIf in doubt, contact our support for assistance. Protecting your eCommerce store is our top priority!",
      "color: white; background: red; font-size: 20px; font-weight: bold; padding: 6px 12px; border-radius: 6px;",
      "color: black; font-size: 16px; line-height: 1.6; margin-top: 8px;",
      "color: black; font-size: 16px; font-weight: bold; text-transform: uppercase;",
      "color: black; font-size: 16px; font-weight: normal;",
      "color: black; font-size: 16px; font-weight: normal;",
      "color: black; font-size: 16px; font-weight: normal;",
      "color: black; font-size: 16px; font-weight: normal;"
    );
  }

  const devToolsOpen = useDetectDevTools();

  if (devToolsOpen) {
    return (
      <div style={{ textAlign: "center", marginTop: "20%", fontFamily: "Arial, sans-serif" }}>
        <h1 style={{ color: "#ff0000" }}>Access Denied</h1>
        <p style={{ fontSize: "18px" }}>
          Please close Developer Tools to access this site or get instructions to enable Developer Tools without any restrictions at: 
          <a href="https://salesbotics.io/enable-dev-tools/" style={{ color: "#007bff", textDecoration: "none" }}> https://salesbotics.io/enable-dev-tools/</a>.
        </p>
      </div>
    );
  }

  const router = createBrowserRouter([
    {
      path: "/",
      element: <Navigate to="dashboard/login" replace />,
    },
    {
      path: "dashboard",
      element: <PrivateRoute />,
      children: [
        {
          element: <RootLayout />,
          children: [
            { index: true, element: <Dashboard /> },
            { path: "products", element: <Products /> },
            { path: "inventory", element: <InventoryMain /> },
            { path: "products/categories", element: <CategoryTable /> },
            { path: "products/color", element: <Color /> },
            { path: "products/product-type", element: <ProductTypesMain /> },
            { path: "products/collection", element: <CollectionTable /> },
            { path: "products/supplier", element: <Supplier /> },
            { path: "products/add-new-product", element: <AddNewProduct /> },
            { path: "products/edit/:id", element: <EditProduct /> },
            { path: "analytics", element: <AnalyticsHome /> },
            { path: "orders", element: <Orders /> },
            { path: "orders/return-request", element: <ReturnRequestMain /> },
            { path: "orders/abandonedcart", element: <AbandonedCart /> },
            { path: "purchase-orders", element: <PurchaseOrder /> },
            { path: "plugin", element: <Plugin /> },
            { path: "customers", element: <Customers /> },
            { path: "stocks", element: <Stocks /> },
            { path: "stocks/reservation", element: <StockReservation /> },
            { path: "stocks/transfers", element: <StockTransfer /> },
            { path: "promocodes", element: <PromoCodes /> },
            { path: "promocodes/usage", element: <PromocodeUsage /> },
            { path: "translations", element: <TranslationMain /> },
            { path: "translations/collections/:languageCode", element: <CollectionTranslation /> },
            { path: "translations/categories/:languageCode", element: <CategoryTranslation /> },
            { path: "translations/products/:languageCode", element: <ProductTranslation /> },
            { path: "translations/tags/:languageCode", element: <TagsTranslation /> },
            { path: "exchangerate", element: <ExchangeRate /> },
            { path: "user-management", element: <UserManagement /> },
            {
              path: "settings",
              element: <SettingsLayout />,
              children: [
                { index: true, element: <General /> },
                { path: "invoice", element: <InvoiceSettings /> },
                { path: "account", element: <Account /> },
                { path: "payment-and-billing", element: <Payment /> },
                { path: "link-account", element: <LinkAccount /> },
                { path: "password", element: <Password /> },
                { path: "push-notification", element: <PushNotification /> },
                { path: "tax-class", element: <TaxClass /> },
                { path: "shipping", element: <Shipping /> },
                { path: "status", element: <Status /> },
                { path: "warehouse", element: <Warehouse /> },
              ]
            },
            { path: "analytics", element: <AnalyticsHome /> },
            {path: "pages", element: <PageList />},
            {path: "pages/editor", element: <PageEditorApp />},
          ]
        }
      ]
    },
    {
      path: "dashboard/login",
      element: <Login />,
    }
  ]);

  return (
    <RouterProvider router={router} />
  );
}

export default App;
